/**
 * RequirePermission — route guard for pages behind a legacy permission ID
 * (`users_permissions.permissionID`, surfaced as `useAuth().hasPermission`).
 *
 * Wrap routes in a pathless <Route element={<RequirePermission permissionId={…} />}>.
 * Unlike RequireModule there is nothing to fetch — permissions ship in the auth
 * payload — so the check is synchronous and fails CLOSED.
 *
 * `fallback` is where a blocked user lands. Default is the safe landing path
 * (the first nav item the account can actually see); pass a nearer parent when
 * the user still has access to the surrounding section — e.g. `/marketing` for
 * a user with Marketing Access but not Marketing Campaigns.
 */
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSafeLandingPath } from '@/hooks/useNavAccess';

export default function RequirePermission({
  permissionId,
  fallback,
}: {
  permissionId: number;
  fallback?: string;
}) {
  const { hasPermission } = useAuth();
  const landing = useSafeLandingPath();

  if (!hasPermission(permissionId)) {
    return <Navigate to={fallback ?? landing} replace />;
  }
  return <Outlet />;
}
